import React from 'react'
import { FormattedMessage } from 'react-intl'
import { Grid, csx } from '@vtex/admin-ui'
import { useHistory } from 'react-router'

import { useDrawer } from '../../../../../../ui/components'
import { ChangeMethodLabel } from './ChangeMethodLabel'
import { ChangeShippingMethodDrawer } from '../ChangeShippingMethodDrawer'
import type { Package } from '../../../../../../core/typings'
import { useShippingPackageItem } from './hooks'
import type { ShippingMethod } from '../../../../constants'
import { UnavailableForPickupAlert } from './UnavailableForPickupAlert'
import { ForcingTakeAwayAlert } from './ForcingTakeAwayAlert'
import { CartSummaryItems } from '../../../../components/CartSummaryItems'
import type { OrderFormItemWithAssemblyItems } from '../../../Cart/typings'
import { NoShippingMethodAvailableAlert } from '../MethodSelectItem'
import { useOrderForm } from '../../../../../../core/requests/checkout'

export type ShippingPackageItemProps = {
  currentPackage: Package
  item: OrderFormItemWithAssemblyItems
  selectedShippingMethod: ShippingMethod
  onItemSlaChange: () => void
}

export const ShippingPackageItem = ({
  currentPackage,
  item,
  selectedShippingMethod,
  onItemSlaChange,
}: ShippingPackageItemProps) => {
  const changeShippingMethodDrawer = useDrawer({
    route: `/checkout/shipping-method-selection/change-shipping-method/${item.orderFormItem.item.id}/${item.orderFormItem.item.index}`,
  })

  const { orderForm } = useOrderForm({
    swrConfig: { revalidateOnMount: true },
  })

  const history = useHistory()

  const {
    shouldShowUnavailableForPickupAlert,
    shouldShowForcingTakeAwayAlert,
    shouldShowUnavailableAlert,
  } = useShippingPackageItem({
    item: item.orderFormItem.item,
    selectedShippingMethod,
  })

  const shouldShowAlert =
    shouldShowUnavailableForPickupAlert ||
    shouldShowForcingTakeAwayAlert ||
    shouldShowUnavailableAlert

  const handleChangeMethodLabelClick = () => {
    changeShippingMethodDrawer.open()
  }

  const handleChangeShippingMethodDrawerClose = () => {
    history.goBack()
  }

  return (
    <div
      className={csx({
        ':not(:last-child)': {
          borderBottom: '$neutral',
        },
      })}
    >
      {shouldShowAlert && (
        <Grid rowGap="0.5rem" className={csx({ marginBottom: '$space-3' })}>
          {shouldShowUnavailableForPickupAlert && (
            <UnavailableForPickupAlert
              itemName={item.orderFormItem.item.name}
            />
          )}
          {shouldShowForcingTakeAwayAlert && (
            <ForcingTakeAwayAlert itemName={item.orderFormItem.item.name} />
          )}
          {shouldShowUnavailableAlert && (
            <NoShippingMethodAvailableAlert
              itemName={item.orderFormItem.item.name}
              postalCode={orderForm?.shippingData.address?.postalCode}
            />
          )}
        </Grid>
      )}
      <div>
        <CartSummaryItems key={item.orderFormItem.index} item={item} />
      </div>
      <ChangeMethodLabel
        currentPackage={currentPackage}
        selectedShippingMethod={selectedShippingMethod}
        onClick={handleChangeMethodLabelClick}
      >
        <FormattedMessage id="checkout.shipping.select-shipping-method.change-delivery-item" />
      </ChangeMethodLabel>
      <ChangeShippingMethodDrawer
        currentPackage={currentPackage}
        isOpen={changeShippingMethodDrawer.isOpen}
        onClose={handleChangeShippingMethodDrawerClose}
        item={item}
        onItemSlaChange={onItemSlaChange}
      />
    </div>
  )
}
